/* eslint-disable react/no-danger */
import { useTranslation } from '@presentation/utils';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import type { FC } from 'react';
import { CountryCode } from '@gql/graphql';
import { InfoBox, InputField, InputLabel, InputSelectField } from '@presentation/components';
import { pedigreeSelectOptions } from '@application/utils';
import * as Styled from '../styled';
import type { MtBreederOnboardingFormValues, MtBreederOnboardingPageProps } from '../types';

export const Step3: FC<Pick<MtBreederOnboardingPageProps, 'breedOptions' | 'breedingNameLabel'>> = (
  props,
) => {
  const { breedOptions, breedingNameLabel } = props;

  const { values } = useFormikContext<MtBreederOnboardingFormValues>();
  const { translate } = useTranslation();

  const breedingNameIsRequired = useMemo(() => {
    return (
      (values?.country === CountryCode.FR && values?.level === '4') ||
      values?.country === CountryCode.CH
    );
  }, [values?.country, values?.level]);

  const breedingNameWarning = useMemo(() => {
    return translate('common.validation.breedingNameRequired');
  }, [translate]);

  const disablePedigreeErrors = useMemo(() => {
    return ![null, undefined, 'FCI'].includes(values?.dogsPedigree);
  }, [values?.dogsPedigree]);

  const dogsPedigreeOptions = useMemo(() => {
    return pedigreeSelectOptions(translate, {
      showText: true,
      showIcons: false,
      value: values.dogsPedigree,
    });
  }, [translate, values.dogsPedigree]);

  return (
    <>
      <Styled.Step3Container>
        <Styled.OnboardingInputWrapper split centered>
          {breedingNameIsRequired && (
            <InfoBox
              dangerouslySetInnerHTML={{
                __html: breedingNameWarning,
              }}
            />
          )}
          <InputLabel requiredColor="red" required={breedingNameIsRequired}>
            <span dangerouslySetInnerHTML={{ __html: breedingNameLabel }} />
          </InputLabel>
          <InputField name="breedingName" />
        </Styled.OnboardingInputWrapper>

        <Styled.OnboardingInputWrapper split centered>
          <InputLabel requiredColor="red" required>
            {translate('common.inputs.raisedBreeds.label')}
          </InputLabel>
          <InputSelectField
            name="dogBreedsIds"
            multiple
            dismissOnClick={false}
            options={breedOptions}
            hasDropdownIcon
            variant="new"
          />
        </Styled.OnboardingInputWrapper>

        <Styled.OnboardingInputWrapper split centered>
          <InputLabel requiredColor="red" required>
            {translate('common.inputs.pedigree.label')}
          </InputLabel>
          <InputSelectField
            useSearch={false}
            name="dogsPedigree"
            options={dogsPedigreeOptions}
            defaultChecked={false}
            required
            hasDropdownIcon
            variant="new"
            disableErrors={disablePedigreeErrors}
          />
        </Styled.OnboardingInputWrapper>

        {values.dogsPedigree != null && values.dogsPedigree !== 'FCI' && (
          <Styled.OnboardingInputWrapper split centered>
            <InputLabel requiredColor="red" required>
              {translate('common.inputs.otherPedigree.label')}
            </InputLabel>
            <InputField name="dogsPedigree" variant="new" />
          </Styled.OnboardingInputWrapper>
        )}
      </Styled.Step3Container>
    </>
  );
};
